import { FC, useState } from "react";
import { useParams, useSearchParams } from "src/router";
import { captureException } from "src/lib/monitoring";
import { useNavigate } from "src/router";
import { Button, useToast } from "@hightouch/htui";
import { AuthLayout } from "./auth-layout";
import { AuthCard } from "./auth-card";
import { useUser } from "src/contexts/user-context";
import { isValidApiRedirectOnLogin } from "./util";
import { buildReturnToUrl } from "src/utils/auth-redirect";

const ERROR_CODE_TO_MESSAGE = {
  ["expired"]: "This link has expired. Please try again.",
  ["redeemed"]: "This link has already been redeemed. Please try again.",
};

const errorCodeToMessage = (errorCode?: string) => {
  return errorCode && Object.keys(ERROR_CODE_TO_MESSAGE).includes(errorCode)
    ? ERROR_CODE_TO_MESSAGE[errorCode]
    : "Something went wrong. Please try again.";
};

export const PasswordlessRedeem: FC = () => {
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const welcome = searchParams.get("welcome");
  const navigate = useNavigate();
  const { code } = useParams();
  const { toast } = useToast();
  const { refetch: refetchUser } = useUser();
  const [isLoading, setIsLoading] = useState(false);

  const handleError = (errorCode?: string) => {
    toast({
      id: "passwordless-redeem-error",
      title: "Login failed",
      message: errorCodeToMessage(errorCode),
      variant: "error",
    });
    navigate(
      returnTo ? buildReturnToUrl("/login/email", returnTo) : "/login/email",
    );
  };

  const login = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/auth/email/${code}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        },
      );
      if (response.ok) {
        if (returnTo && isValidApiRedirectOnLogin(returnTo)) {
          window.location.href = returnTo;
          return;
        }

        await refetchUser();
        if (returnTo) {
          navigate(returnTo);
        } else if (welcome === "true") {
          navigate("/welcome");
        } else {
          navigate("/");
        }
      } else {
        const data = await response.json();
        handleError(data?.error_code);
      }
    } catch (e) {
      captureException(e);
      handleError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <AuthCard heading="Welcome to Hightouch">
        <Button
          onClick={login}
          isLoading={isLoading}
          isDisabled={isLoading}
          variant="primary"
          size="xl"
        >
          Continue to your workspace
        </Button>
      </AuthCard>
    </AuthLayout>
  );
};
